import { FC, useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Card, ImportButton, CenteredSpin, IFile } from '../ui'
import { Box } from '../ui/Box/Box'
import { useSelector } from 'react-redux'
import { ButtonsBlock } from '../Edit/components'
import { Alert, Divider } from 'antd'
import DocumentsBlock from '../AdminUploading/components/DocumentsBlock/DocumentsBlock'
import GenerationAttributesPayNotEnoughModal from '../GenerationAttributes/components/GenerationAttributesPayNotEnoughModal/GenerationAttributesPayNotEnoughModal'
import style from './style.m.less'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useRequest, { RequestTypes } from '../hooks/useRequest'
import { sendRealization, getFromFile } from './config'
import { ArrowLeft, AddCircle } from '../../assets/icons'
import { signBody, currencyFormat, isExpired } from '../helpers'
import { ConsumerBlock, PaymentModal } from './components'
import { useGetTariffRate } from '../hooks'
import IAppState from '../../store/states'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { EUserType } from '../../constants/approval'

const addingOptions = [
    { label: 'Вручную', id: '8q7f77bf-a3ec-4441-9b13-e46eabc3c027' },
    { label: 'Из файла', id: 'jr324494-8d32-423a-a7d8-b038ece4a70b' },
]

interface IRealizationAG {
    isWithPayment?: boolean
}

const RealizationAG: FC<IRealizationAG> = ({ isWithPayment }) => {
    const { t } = useTranslation()
    const { state } = useLocation()
    const { contract_id, contract_number, period_date, fee, volume } = state
    const navigate = useNavigate()
    const [option, setOption] = useState(addingOptions[0].label)
    const [total, setTotal] = useState(0)
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false)
    const [isPayNotEnoughModalVisible, setIsPayNotEnoughModalVisible] = useState<boolean>(false)
    const [selectedRepayments, setSelectedRepayments] = useState<any>([{ user_id: '', volume: 0 }])
    const [files, setFiles] = useState<IFile[]>([])
    const [stFile, stSetFile] = useState<File>()
    const [confirmClicked, setConfirmClicked] = useState<boolean>(false)

    const fileHandler = (list: any) => {
        setFiles(list)
    }

    const isManual = option === 'Вручную'

    const { fetch: fetchConsumers } = useRequest(getFromFile)

    const { fetch: fetchRepayment } = useRequest({
        link: 'api/extension/v1/users/gc_repayments',
        method: RequestTypes.post,
    })

    useEffect(() => {
        if (files.length) {
            const body = {
                document_id: files[0].id,
                contract_number,
            }
            fetchConsumers({
                body,
                onSuccess: (data) => {
                    setTotal(data?.consumed_volume)
                    setSelectedRepayments(data?.consumers)
                },
                errorCodeMessage: true,
            })
        }
    }, [files])

    const { isClient, isGOOwner, isTSO } = useCurrentUser()
    const userInfo = useSelector((store: IAppState) => store.user)
    const { data } = userInfo
    const isClientGOOwnerTSO = isClient || isGOOwner || isTSO
    const isSignExpired = isClientGOOwnerTSO && isExpired(data?.certificate_data.token_expiration)
    // У пользователя должна быть МЧД
    const needAttorney =
        data?.user_type === EUserType.UrLico && isClientGOOwnerTSO && !data?.without_attorney
    // У пользователя должна быть МЧД, но её нет
    const isEmptyAttorney = needAttorney && !data?.machine_readable_attorney
    // У пользователя есть МЧД и у неё истёк срок
    const isAttorneyExpired =
        needAttorney &&
        !!data?.machine_readable_attorney &&
        isExpired(data?.machine_readable_attorney.validity_period, true)

    const stateRef = useRef()
    stateRef.current = selectedRepayments

    const updateSelectedRepayments = (index: number, key: string, value: number) => {
        const arrayCopy = !stateRef?.current?.[index]
            ? [...stateRef.current, { user_id: '', volume: 0 }]
            : [...stateRef.current]
        arrayCopy[index][key] = value
        if (key === 'consumer_data') {
            delete arrayCopy[index]['user_id']
        }
        setSelectedRepayments(arrayCopy)
    }
    const stFileFunction = (file: File) => {
        stSetFile(file)
    }
    const hasZeroVolume = selectedRepayments.some((item) => item.volume === 0)

    useEffect(() => {
        const res = selectedRepayments.reduce((sum, current) => sum + current.volume, 0)
        setTotal(res)
    }, [selectedRepayments])

    const [componentsArray, setComponentsArray] = useState<any>([
        <ConsumerBlock
            totalVolume={volume}
            updateRepayments={updateSelectedRepayments}
            index={0}
        />,
    ])

    const incrementComponentsArray = () => {
        setComponentsArray([
            ...componentsArray,
            <ConsumerBlock
                totalVolume={volume}
                updateRepayments={updateSelectedRepayments}
                index={componentsArray.length}
            />,
        ])
    }

    const { fetch } = useRequest(sendRealization)

    const onChange = (value: string) => {
        setOption(value)
    }

    const getBack = () => {
        navigate(-1)
    }

    const confirmRepayment = async () => {
        try {
            setConfirmClicked(true)
            if (isWithPayment) {
                setIsPaymentModalVisible(true)
                return
            }
            const body = {
                contract_number,
                period_id: contract_id,
                period_date,
                consumers: selectedRepayments,
            }
            const { request_id, signature } = await signBody(body, data)
            fetch({
                body,
                getParams: {
                    request_id,
                },
                config: {
                    headers: {
                        Signature: signature,
                    },
                },
                successMessage: {
                    title: t('detailedCard.success'),
                    description: 'Реализация АГ по ЗД прошла успешно',
                },
                onSuccess: () => navigate(-1),
                onError: () => setConfirmClicked(false),
                showError: true,
            })
        } catch (e) {
            console.error(e)
        } finally {
            console.log('finally')
            setConfirmClicked(false)
        }

        /*
        const formdata = new FormData()
        formdata.append('file', stFile)
        fetchRepayment({
            body: formdata,
            onError: (err: any) => {
                message.error(err.detail)
            },
            showError: false,
            onSuccess: async () => {
                const { request_id, signature } = await signBody(body, data)
                fetch({
                    body,
                    getParams: {
                        request_id,
                    },
                    config: {
                        headers: {
                            Signature: signature,
                        },
                    },
                    successMessage: {
                        title: t('detailedCard.success'),
                        description: 'Реализация АГ по ЗД прошла успешно',
                    },
                    onSuccess: () => navigate(-1),
                    showError: true,
                })
            },
        })
        */
    }

    const { loadingTariffs, rate } = useGetTariffRate({
        id: 67,
        volume,
    })

    if (loadingTariffs) {
        return <CenteredSpin />
    }

    return (
        <div className={style.container}>
            <div className={style.titleWrapper}>
                <span className={style.backLink} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </span>
                <div className={style.mainHeader}>Реализация атрибутов генерации</div>
            </div>

            <Card className={style.repaymentCard}>
                <div className={style.title}>Как Вы хотите добавить потребителей?</div>
                <Box direction="row" gap={'16px'}>
                    {addingOptions.map((item) => {
                        const { label, id } = item
                        return (
                            <>
                                <div
                                    className={
                                        option === label ? style.greenToolSelected : style.greenTool
                                    }
                                    key={id}
                                    onClick={() => onChange(label)}
                                >
                                    {label}
                                </div>
                            </>
                        )
                    })}
                </Box>
                {isManual ? (
                    <>
                        {componentsArray}
                        <ImportButton
                            value="Добавить потребителя"
                            icon={<AddCircle />}
                            onClick={incrementComponentsArray}
                        />
                    </>
                ) : (
                    <DocumentsBlock
                        value={files}
                        onChange={fileHandler}
                        setFile={stFileFunction}
                        hideComment
                        serviceId={4}
                        description="Загрузите файл, из которого хотите импортировать данные"
                        customStyle={{ width: '440px', marginTop: '24px' }}
                    />
                )}
                <Divider />
                <div className={style.repaymentItemWrapper}>
                    <div className={style.repaymentItemName}>Общий объем</div>
                    <div className={style.repaymentItemValue}>{`${currencyFormat(
                        volume,
                    )} кВт*ч`}</div>
                </div>
                <div className={style.repaymentItemWrapper}>
                    <div className={style.repaymentItemName}>Реализуемый объем</div>
                    <div className={style.repaymentItemValue}>
                        {!total ? '-' : `${currencyFormat(total)} кВт*ч`}
                    </div>
                </div>
                {isWithPayment && (
                    <div className={style.repaymentItemWrapper}>
                        <div className={style.repaymentItemName}>Сумма к оплате</div>
                        <div className={style.repaymentItemValue}>{`${currencyFormat(fee)} ₽`}</div>
                    </div>
                )}
                {isSignExpired && (
                    <Alert
                        className={style.signError}
                        message={'Истек срок действия ЭП'}
                        type="error"
                        showIcon
                    />
                )}
                {(isEmptyAttorney || isAttorneyExpired) && (
                    <Alert
                        className={style.signError}
                        message={
                            isEmptyAttorney
                                ? t('profile.attorneyIsMissing')
                                : t('profile.attorneyIsExpired')
                        }
                        type="error"
                        showIcon
                    />
                )}
                <Divider />
                <ButtonsBlock
                    confirmTitle={t('greenToolsModal.signAndImplement')}
                    confirmDisabled={
                        !(volume === total) ||
                        isSignExpired ||
                        isEmptyAttorney ||
                        isAttorneyExpired ||
                        confirmClicked ||
                        hasZeroVolume
                    }
                    cancelAction={getBack}
                    confirmAction={confirmRepayment}
                />
            </Card>
            <PaymentModal
                isModalVisible={isPaymentModalVisible}
                data={state}
                consumers={selectedRepayments}
                onOpenSubModal={() => {
                    setIsPaymentModalVisible(false)
                    setIsPayNotEnoughModalVisible(true)
                }}
                onCancel={() => {
                    setIsPaymentModalVisible(false)
                }}
            />
            <GenerationAttributesPayNotEnoughModal
                isModalVisible={isPayNotEnoughModalVisible}
                onClose={() => setIsPayNotEnoughModalVisible(false)}
            />
        </div>
    )
}

export default RealizationAG
